import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { adminApi, unwrap, type CreateAdminOrderPayload } from '../../services/api'
import { useColors } from '../../hooks/useColors'
import { useModalA11y } from '../../hooks/useModalA11y'
import OrderQRCode from './OrderQRCode'
import { openPrintLabels } from './PrintLabel'
import type { QrPayload } from './orderQr'
import { istTomorrow } from '../../utils/dateIST'
import {
  collectManualRateErrors,
  effectiveUnitPrice,
  isManualPriceEligible,
  previewLineTotal,
  validateManualRate,
} from './manualPricing'

// When present, the wizard runs in EDIT mode: it reuses the same item/pricing/
// schedule UI but seeds from an existing order and PATCHes it (same Order ID)
// instead of creating a new one.
export type EditOrderContext = {
  orderId: string
  orderNumber: string
  customer: Customer
  address?: Address | null
  items: Array<{ service_item_id: string; quantity: number; item_count?: number | null }>
  pickupDate?: string
  pickupSlot?: string
  deliveryDate?: string
  deliverySlot?: string
  paymentMode?: 'cod' | 'online'
  adminNotes?: string
  specialInstructions?: string
  couponCode?: string
}

type CreateOrderModalProps = {
  isOpen: boolean
  onClose: () => void
  onCreated: () => void
  editContext?: EditOrderContext | null
}

type Customer = {
  id: string
  name?: string | null
  phone?: string | null
  email?: string | null
}

type SearchResultCustomer = Customer & {
  orders_count?: number | string | null
  last_order_at?: string | null
}

type Address = {
  id: string
  full_address?: string | null
  label?: string | null
  is_default?: boolean | null
}

type SlotInfo = {
  label: string
  available?: boolean
  is_full?: boolean
}

type CreatedOrder = {
  id?: string
  order_number?: string
  display_id?: string | null
  qr_token?: string | null
  qr_payload?: QrPayload | null
  customerName?: string
  items?: Array<{ name: string; quantity: number; item_count?: number | null }>
}

type ServiceItem = {
  id: string
  name: string
  price: number | string
  unit?: string | null
  category?: string | null
  service_name?: string | null
}

const CATEGORY_ORDER = ['men', 'women', 'kids', 'household', 'misc']
const CATEGORY_LABELS: Record<string, string> = {
  men: 'Men',
  women: 'Women',
  kids: 'Kids',
  household: 'Home',
  misc: 'More',
}

const formatUnit = (unit?: string | null) => {
  if (!unit) return ''
  if (unit === 'per_item') return 'per item'
  if (unit === 'per_kg') return 'per kg'
  return unit.replace(/_/g, ' ')
}

// Decimal-weight support: per-kg items carry a fractional weight (e.g. 4.25 kg);
// every other unit is a whole garment count. The +/- buttons step per-kg items
// by 0.5, but admins can also type an exact weight into the input.
const isWeightUnit = (unit?: string | null) => String(unit || '').toLowerCase() === 'per_kg'

// Operator-agreed per-kg rates. Eligibility + validation mirror the backend
// (utils/manual-price.js); the backend still re-validates and re-computes.

const stepForUnit = (unit?: string | null) => (isWeightUnit(unit) ? 0.5 : 1)
const roundQty = (n: number) => Math.round(n * 100) / 100
// Trim trailing zeros so 3.5, 3.75 and 4 read naturally.
const formatQty = (n: number) => String(roundQty(n))


const normalizePhone = (raw: string) => {
  let cleaned = raw.replace(/[\s\-()]/g, '')
  cleaned = cleaned.replace(/^\+91/, '')
  cleaned = cleaned.replace(/^0+/, '')
  return cleaned
}

const STEPS = ['Customer & Location', 'Items', 'Schedule', 'Review']
// Minimum-bill / delivery-fee rule - MUST mirror the backend pricing engine
// (order.controller.js + admin.controller.js). The customer always pays at least
// ₹149: at/above the floor delivery is free, below it the delivery fee fills the
// gap. This replaces the old static settings.delivery_fee preview which diverged
// from the server-computed total.
const MIN_PAYABLE_FLOOR = 149
// Fallback slot labels used only if live slot availability can't be loaded.
const PICKUP_SLOTS = ['9AM-11AM', '11AM-1PM', '2PM-4PM', '4PM-6PM']

// Show paise only when the amount is fractional (decimal-weight pricing yields
// ₹346.50); whole amounts stay clean (₹297). Rounded to 2dp first to avoid
// binary-float artefacts.
const formatAmount = (value: number) => {
  const rounded = Math.round(Number(value || 0) * 100) / 100
  const hasPaise = Math.round(rounded * 100) % 100 !== 0
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: hasPaise ? 2 : 0,
  }).format(rounded)
}

const getTomorrow = () => istTomorrow()

export default function CreateOrderModal({ isOpen, onClose, onCreated, editContext = null }: CreateOrderModalProps) {
  const colors = useColors()
  const wizardRef = useModalA11y(isOpen, onClose)
  const isEdit = Boolean(editContext)
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isPlacing, setIsPlacing] = useState(false)
  const [error, setError] = useState('')

  const [phone, setPhone] = useState('')
  const [, setSearchResult] = useState<Customer | null>(null)
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [address, setAddress] = useState<Address | null>(null)
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false)

  // Typeahead search state
  const [searchResults, setSearchResults] = useState<SearchResultCustomer[]>([])
  const [searching, setSearching] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchError, setSearchError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const [customerForm, setCustomerForm] = useState({
    name: '',
    email: '',
    address_line: '',
    city: 'Gurugram',
    pincode: '',
  })

  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([])
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({})
  // Operator-agreed per-kg rates, keyed by service_item id. A key being PRESENT
  // means this line is in manual mode; absent means the catalog rate applies.
  // Kept as the raw string so a half-typed "1" never becomes a real rate.
  const [manualRates, setManualRates] = useState<Record<string, string>>({})
  const [activeService, setActiveService] = useState<string>('all')
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [pricingMode, setPricingMode] = useState<'weight' | 'item'>('item')
  const [itemSearch, setItemSearch] = useState('')

  const [pickupDate, setPickupDate] = useState(getTomorrow())
  const [pickupSlot, setPickupSlot] = useState(PICKUP_SLOTS[0])
  const [deliveryDate, setDeliveryDate] = useState('')
  const [deliverySlot, setDeliverySlot] = useState(PICKUP_SLOTS[2])
  const [paymentMode, setPaymentMode] = useState<'cod' | 'online'>('cod')
  const [adminNotes, setAdminNotes] = useState('')
  const [specialInstructions, setSpecialInstructions] = useState('')

  const [couponCode, setCouponCode] = useState('')
  const [couponApplied, setCouponApplied] = useState(false)
  const [, setCouponDiscount] = useState(0)
  const [couponError, setCouponError] = useState('')

  // Delivery location (address-on-behalf)
  const [addresses, setAddresses] = useState<Address[]>([])
  const [loadingAddresses, setLoadingAddresses] = useState(false)
  const [showNewAddressForm, setShowNewAddressForm] = useState(false)
  const [savingAddress, setSavingAddress] = useState(false)
  const [addressForm, setAddressForm] = useState({
    line1: '',
    line2: '',
    area: '',
    landmark: '',
    pincode: '',
    city: 'Gurugram',
  })

  // Live slot availability
  const [pickupSlotOptions, setPickupSlotOptions] = useState<SlotInfo[]>([])
  const [deliverySlotOptions, setDeliverySlotOptions] = useState<SlotInfo[]>([])
  // Track whether the live /slots fetch actually succeeded, so we can show
  // "No slots available" on an empty-but-loaded date instead of silently
  // falling back to the hardcoded list (we only fall back on a failed fetch).
  const [pickupSlotsLoaded, setPickupSlotsLoaded] = useState(false)
  const [deliverySlotsLoaded, setDeliverySlotsLoaded] = useState(false)
  const [pickupHoliday, setPickupHoliday] = useState(false)
  const [deliveryHoliday, setDeliveryHoliday] = useState(false)

  const [createdOrder, setCreatedOrder] = useState<CreatedOrder | null>(null)

  // Edit mode: seed the wizard from the existing order and jump straight to the
  // Items step (the customer is fixed). Quantities are keyed by service_item_id,
  // so seeding works even before the service-item catalogue finishes loading.
  useEffect(() => {
    if (!isOpen || !editContext) return
    setError('')
    setCreatedOrder(null)
    setCustomer(editContext.customer)
    if (editContext.address) setAddress(editContext.address)
    const seededQty: Record<string, number> = {}
    const seededCounts: Record<string, number> = {}
    for (const it of editContext.items) {
      seededQty[it.service_item_id] = Number(it.quantity) || 0
      if (it.item_count != null && Number.isFinite(Number(it.item_count))) {
        seededCounts[it.service_item_id] = Math.max(0, Math.trunc(Number(it.item_count)))
      }
    }
    setQuantities(seededQty)
    setItemCounts(seededCounts)
    if (editContext.pickupDate) setPickupDate(editContext.pickupDate)
    if (editContext.pickupSlot) setPickupSlot(editContext.pickupSlot)
    if (editContext.deliveryDate) setDeliveryDate(editContext.deliveryDate)
    if (editContext.deliverySlot) setDeliverySlot(editContext.deliverySlot)
    if (editContext.paymentMode) setPaymentMode(editContext.paymentMode)
    if (editContext.adminNotes) setAdminNotes(editContext.adminNotes)
    setSpecialInstructions(editContext.specialInstructions || '')
    if (editContext.couponCode) {
      setCouponCode(editContext.couponCode)
      setCouponApplied(true)
    }
    setStep(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editContext?.orderId])

  const groupedItems = useMemo(() => {
    return serviceItems.reduce<Record<string, ServiceItem[]>>((acc, item) => {
      const key = item.service_name || 'Other'
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(item)
      return acc
    }, {})
  }, [serviceItems])

  const serviceNames = useMemo(() => Object.keys(groupedItems), [groupedItems])

  const categoriesByService = useMemo(() => {
    const map: Record<string, string[]> = {}
    for (const [svc, items] of Object.entries(groupedItems)) {
      const present = new Set<string>()
      for (const it of items) {
        if (it.category) present.add(it.category)
      }
      if (present.size === 0) {
        map[svc] = []
        continue
      }
      const ordered = CATEGORY_ORDER.filter((c) => present.has(c))
      const extras = [...present].filter((c) => !CATEGORY_ORDER.includes(c))
      map[svc] = [...ordered, ...extras]
    }
    return map
  }, [groupedItems])

  const activeServiceCategories = useMemo(() => {
    if (activeService === 'all') return []
    return categoriesByService[activeService] || []
  }, [activeService, categoriesByService])

  const unitsByService = useMemo(() => {
    const map: Record<string, { hasPerKg: boolean; hasPerItem: boolean }> = {}
    for (const [svc, items] of Object.entries(groupedItems)) {
      map[svc] = {
        hasPerKg: items.some((it) => it.unit === 'per_kg'),
        hasPerItem: items.some((it) => it.unit === 'per_item'),
      }
    }
    return map
  }, [groupedItems])

  const activeServiceHasPricingToggle = useMemo(() => {
    if (activeService === 'all') return false
    const u = unitsByService[activeService]
    return Boolean(u && u.hasPerKg && u.hasPerItem)
  }, [activeService, unitsByService])

  useEffect(() => {
    if (!activeServiceHasPricingToggle) {
      setPricingMode('item')
    }
  }, [activeServiceHasPricingToggle])

  useEffect(() => {
    if (activeService === 'all') {
      setActiveCategory('all')
      return
    }
    const cats = categoriesByService[activeService] || []
    if (cats.length === 0) {
      setActiveCategory('all')
    } else if (activeCategory !== 'all' && !cats.includes(activeCategory)) {
      setActiveCategory('all')
    }
  }, [activeService, categoriesByService, activeCategory])

  const filteredGroups = useMemo(() => {
    const q = itemSearch.trim().toLowerCase()
    const entries = Object.entries(groupedItems)
      .filter(([name]) => activeService === 'all' || name === activeService)
      .map(([name, items]) => {
        let filtered = items
        const u = unitsByService[name]
        const isToggleService = activeService === name && u && u.hasPerKg && u.hasPerItem
        if (!q && isToggleService) {
          filtered = filtered.filter((it) =>
            pricingMode === 'weight' ? it.unit === 'per_kg' : it.unit === 'per_item',
          )
        }
        if (
          !q &&
          activeService !== 'all' &&
          activeCategory !== 'all' &&
          (categoriesByService[name] || []).length > 0 &&
          !(isToggleService && pricingMode === 'weight')
        ) {
          filtered = filtered.filter((it) => it.category === activeCategory)
        }
        if (q) {
          filtered = filtered.filter(
            (it) =>
              it.name?.toLowerCase().includes(q) ||
              (it.service_name || '').toLowerCase().includes(q) ||
              (it.category || '').toLowerCase().includes(q),
          )
        }
        return [name, filtered] as const
      })
      .filter(([, items]) => items.length > 0)
    return entries
  }, [groupedItems, activeService, activeCategory, itemSearch, categoriesByService, unitsByService, pricingMode])

  const serviceSelectedCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    let total = 0
    for (const item of serviceItems) {
      const qty = quantities[item.id] || 0
      if (qty > 0) {
        const key = item.service_name || 'Other'
        counts[key] = (counts[key] || 0) + qty
        total += qty
      }
    }
    return { counts, total }
  }, [serviceItems, quantities])

  const selectedItems = useMemo(() => {
    return serviceItems
      .map((item) => ({
        ...item,
        quantity: quantities[item.id] || 0,
        item_count: itemCounts[item.id],
      }))
      .filter((item) => item.quantity > 0)
  }, [serviceItems, quantities, itemCounts])

  const subtotal = useMemo(() => {
    // Preview only - the server re-derives this from the catalog and the
    // validated manual rate, so the stored total always wins.
    return selectedItems.reduce(
      (acc, item) => acc + previewLineTotal(item, item.quantity, manualRates),
      0,
    )
  }, [selectedItems, manualRates])

  /** Manual rates that are present but invalid. Non-empty blocks submission. */
  const manualRateErrors = useMemo(
    () => collectManualRateErrors(serviceItems, manualRates),
    [serviceItems, manualRates],
  )
  const hasManualRateError = Object.keys(manualRateErrors).length > 0

  const setManualMode = (itemId: string, on: boolean, catalogPrice: number) => {
    setManualRates((prev) => {
      const next = { ...prev }
      if (on) next[itemId] = String(catalogPrice)
      else delete next[itemId]
      return next
    })
  }

  // Delivery fee follows the ₹149 payable-floor rule, exactly like the customer
  // app and the server. subtotal=0 (no items yet) shows 0 so the review doesn't
  // display a phantom fee. The server re-derives this on submit, so the preview
  // and the stored/invoiced total always agree.
  const deliveryFee = useMemo(() => {
    if (subtotal <= 0) return 0
    return subtotal >= MIN_PAYABLE_FLOOR ? 0 : MIN_PAYABLE_FLOOR - subtotal
  }, [subtotal])

  const grandTotal = useMemo(() => Math.max(0, subtotal + deliveryFee), [subtotal, deliveryFee])

  const loadServices = useCallback(async () => {
    try {
      setError('')
      // Use the "full" endpoint (all active services + their items, no
      // pagination) and flatten to priced items. /admin/services is paginated
      // and double-nests its payload, which the wizard can't consume.
      const response = await adminApi.getServicesFull()
      const services = unwrap<Array<{ name: string; is_active?: boolean; items?: ServiceItem[] }>>(response)
      const items = Array.isArray(services)
        ? services
            .filter((svc) => svc?.is_active !== false)
            .flatMap((svc) =>
              (svc.items || [])
                .filter((it) => (it as { is_active?: boolean })?.is_active !== false)
                .map((it) => ({ ...it, service_name: svc.name })),
            )
        : []
      setServiceItems(items)
    } catch {
      setError('Unable to load services. Please try again.')
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    void loadServices()
  }, [isOpen, loadServices])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    if (step === 3 && couponCode.trim()) {
      setCouponApplied(true)
    }
  }, [isOpen, step, couponCode])

  useEffect(() => {
    if (pickupDate) {
      const pickup = new Date(pickupDate)
      pickup.setDate(pickup.getDate() + 2)
      setDeliveryDate(pickup.toISOString().slice(0, 10))
    }
  }, [pickupDate])

  // Live pickup slot availability for the chosen pickup date.
  useEffect(() => {
    if (!isOpen || !pickupDate) return
    let cancelled = false
    const load = async () => {
      try {
        const response = await adminApi.getSlots(pickupDate)
        const data = unwrap<{ is_holiday?: boolean; slots?: SlotInfo[] }>(response)
        if (cancelled) return
        setPickupHoliday(Boolean(data?.is_holiday))
        const slots = Array.isArray(data?.slots) ? data.slots : []
        setPickupSlotOptions(slots)
        setPickupSlotsLoaded(true)
        const firstAvail = slots.find((s) => s.available !== false && !s.is_full)
        if (firstAvail) setPickupSlot(firstAvail.label)
      } catch {
        if (!cancelled) {
          setPickupHoliday(false)
          setPickupSlotOptions([])
          setPickupSlotsLoaded(false)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [isOpen, pickupDate])

  // Live delivery slot availability for the chosen delivery date.
  useEffect(() => {
    if (!isOpen || !deliveryDate) return
    let cancelled = false
    const load = async () => {
      try {
        const response = await adminApi.getSlots(deliveryDate)
        const data = unwrap<{ is_holiday?: boolean; slots?: SlotInfo[] }>(response)
        if (cancelled) return
        setDeliveryHoliday(Boolean(data?.is_holiday))
        const slots = Array.isArray(data?.slots) ? data.slots : []
        setDeliverySlotOptions(slots)
        setDeliverySlotsLoaded(true)
        const firstAvail = slots.find((s) => s.available !== false && !s.is_full)
        if (firstAvail) setDeliverySlot(firstAvail.label)
      } catch {
        if (!cancelled) {
          setDeliveryHoliday(false)
          setDeliverySlotOptions([])
          setDeliverySlotsLoaded(false)
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [isOpen, deliveryDate])

  const loadAddresses = async (customerId: string, preselectId?: string) => {
    setLoadingAddresses(true)
    try {
      const response = await adminApi.getCustomerAddresses(customerId)
      const data = unwrap<{ addresses?: Address[] }>(response)
      const list = Array.isArray(data?.addresses) ? data.addresses : []
      setAddresses(list)
      const preselect =
        (preselectId && list.find((a) => a.id === preselectId)) ||
        list.find((a) => a.is_default) ||
        list[0] ||
        null
      setAddress(preselect)
      setShowNewAddressForm(list.length === 0)
    } catch {
      setAddresses([])
      setShowNewAddressForm(true)
    } finally {
      setLoadingAddresses(false)
    }
  }

  const handleSaveAddress = async () => {
    if (!customer) return
    const { line1, line2, area, pincode } = addressForm
    if (!line1.trim() && !line2.trim() && !area.trim()) {
      setError('Enter at least a flat/building, street, or area for the delivery location.')
      return
    }
    if (pincode.trim() && !/^\d{6}$/.test(pincode.trim())) {
      setError('Pincode must be 6 digits.')
      return
    }
    setSavingAddress(true)
    setError('')
    try {
      const response = await adminApi.createCustomerAddress(customer.id, {
        line1: addressForm.line1.trim() || undefined,
        line2: addressForm.line2.trim() || undefined,
        area: addressForm.area.trim() || undefined,
        landmark: addressForm.landmark.trim() || undefined,
        pincode: addressForm.pincode.trim() || undefined,
        city: addressForm.city.trim() || undefined,
      })
      const created = unwrap<Address>(response)
      setAddresses((prev) => [created, ...prev])
      setAddress(created)
      setShowNewAddressForm(false)
      setAddressForm({ line1: '', line2: '', area: '', landmark: '', pincode: '', city: 'Gurugram' })
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Unable to save delivery location. Please try again.'
      )
    } finally {
      setSavingAddress(false)
    }
  }

  useEffect(() => {
    if (!isOpen) {
      return
    }
    // In edit mode the seed effect owns the initial state - don't blow it away.
    if (editContext) {
      return
    }

    setStep(0)
    setError('')
    setSearchResult(null)
    setCustomer(null)
    setAddress(null)
    setShowNewCustomerForm(false)
    setSearchResults([])
    setSearching(false)
    setSearchQuery('')
    setSearchError('')
    setHasSearched(false)
    setQuantities({})
    setItemCounts({})
    setActiveService('all')
    setActiveCategory('all')
    setPricingMode('item')
    setItemSearch('')
    setPickupDate(getTomorrow())
    setPickupSlot(PICKUP_SLOTS[0])
    setDeliveryDate('')
    setDeliverySlot(PICKUP_SLOTS[2])
    setPaymentMode('cod')
    setAdminNotes('')
    setSpecialInstructions('')
    setCouponCode('')
    setCouponApplied(false)
    setCouponDiscount(0)
    setCouponError('')
    setAddresses([])
    setLoadingAddresses(false)
    setShowNewAddressForm(false)
    setSavingAddress(false)
    setAddressForm({ line1: '', line2: '', area: '', landmark: '', pincode: '', city: 'Gurugram' })
    setPickupSlotOptions([])
    setDeliverySlotOptions([])
    setPickupSlotsLoaded(false)
    setDeliverySlotsLoaded(false)
    setPickupHoliday(false)
    setDeliveryHoliday(false)
    setCreatedOrder(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Debounced typeahead: fire search 400ms after the admin stops typing,
  // cancel stale requests via AbortController.
  useEffect(() => {
    const q = searchQuery.trim()
    if (!q || q.length < 3 || customer) {
      if (!q) {
        setSearchResults([])
        setHasSearched(false)
        setSearchError('')
      }
      return
    }

    const timer = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setSearching(true)
      setSearchError('')
      try {
        const response = await adminApi.searchCustomersQuick(normalizePhone(q))
        if (controller.signal.aborted) return
        const items = Array.isArray(response?.data) ? response.data : []
        setSearchResults(items)
        setHasSearched(true)
        setShowNewCustomerForm(false)
        setSearchResult(null)
      } catch (err: any) {
        if (controller.signal.aborted) return
        setSearchError(
          err?.response?.data?.message || err?.message || 'Search failed. Try again.'
        )
      } finally {
        if (!controller.signal.aborted) setSearching(false)
      }
    }, 400)

    return () => {
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, customer])

  const handleSearchCustomer = async () => {
    const q = (searchQuery || phone).trim()
    if (!q) {
      setError('Please enter a phone number, name, or email to search.')
      return
    }

    abortRef.current?.abort()
    setLoading(true)
    setError('')
    setSearchResult(null)
    setShowNewCustomerForm(false)
    setSearchResults([])
    setSearchError('')

    try {
      const response = await adminApi.searchCustomersQuick(normalizePhone(q))
      const items = Array.isArray(response?.data) ? response.data : []
      setSearchResults(items)
      setHasSearched(true)

      if (items.length === 0) {
        setShowNewCustomerForm(true)
        setCustomerForm((prev) => ({ ...prev, name: '', email: '' }))
      }
    } catch (err: any) {
      const message = err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Unable to search customer. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateCustomer = async () => {
    if (!customerForm.name.trim() || !phone.trim()) {
      setError('Please provide customer name and phone number.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await adminApi.createCustomer({
        name: customerForm.name.trim(),
        phone: phone.trim(),
        email: customerForm.email.trim() || undefined,
        address_line: customerForm.address_line.trim() || undefined,
        city: customerForm.city.trim() || undefined,
        pincode: customerForm.pincode.trim() || undefined,
      })

      const created = unwrap<Customer & { address?: Address }>(response)
      setCustomer(created)
      // Stay on step 0 - the Location section below lets staff pick/add a
      // delivery address before continuing to items.
      await loadAddresses(created.id, created?.address?.id)
    } catch (err: any) {
      const message = err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Unable to create customer. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }


  const handleChangeCustomer = () => {
    setCustomer(null)
    setAddress(null)
    setAddresses([])
    setShowNewAddressForm(false)
    setSearchResult(null)
    setShowNewCustomerForm(false)
    setSearchResults([])
    setSearchQuery('')
    setHasSearched(false)
    setSearchError('')
    setPhone('')
    setError('')
  }

  const handleQuantityChange = (itemId: string, delta: number) => {
    setQuantities((prev) => {
      const nextValue = Math.max(0, roundQty((prev[itemId] || 0) + delta))
      return { ...prev, [itemId]: nextValue }
    })
  }

  // Direct decimal entry for per-kg items (admin types e.g. 4.25). Clamps to a
  // non-negative 2dp value; blank/invalid resets the line to 0 (deselected).
  const setQuantityDirect = (itemId: string, raw: string) => {
    const n = parseFloat(raw)
    setQuantities((prev) => ({
      ...prev,
      [itemId]: Number.isFinite(n) && n > 0 ? roundQty(n) : 0,
    }))
  }

  const handleItemCountChange = (itemId: string, delta: number) => {
    setItemCounts((prev) => {
      const nextValue = Math.max(0, Math.min(1000, Math.trunc((prev[itemId] || 0) + delta)))
      return { ...prev, [itemId]: nextValue }
    })
  }

  const setItemCountDirect = (itemId: string, raw: string) => {
    const digits = raw.replace(/\D/g, '')
    setItemCounts((prev) => ({
      ...prev,
      [itemId]: digits ? Math.min(1000, Math.trunc(Number(digits))) : 0,
    }))
  }

  const handlePlaceOrder = async () => {
    setIsPlacing(true)
    if (!customer) {
      setError('Please select a customer before placing an order.')
      setIsPlacing(false)
      return
    }

    if (selectedItems.length === 0) {
      setError('Please select at least one item.')
      setIsPlacing(false)
      return
    }

    setLoading(true)
    setError('')

    const toLinePayload = (item: (typeof selectedItems)[number]) => {
      const line: {
        service_item_id: string
        quantity: number
        item_count?: number
        unit_price?: number
      } = {
        service_item_id: item.id,
        quantity: item.quantity,
      }
      if (isWeightUnit(item.unit) && item.item_count != null) {
        line.item_count = Math.max(0, Math.trunc(Number(item.item_count)))
      }
      // Only an eligible line in manual mode carries a rate. We send the RATE,
      // never a line total - the backend multiplies.
      const draft = manualRates[item.id]
      if (draft !== undefined && isManualPriceEligible(item)) {
        const parsed = validateManualRate(draft)
        if (parsed.ok) line.unit_price = parsed.value
      }
      return line
    }

    try {
      const selectedAddressId = address?.id?.trim()

      // EDIT MODE: PATCH the existing order (same Order ID, timeline, history).
      if (isEdit && editContext) {
        await adminApi.editOrder(editContext.orderId, {
          items: selectedItems.map(toLinePayload),
          pickup_date: pickupDate,
          pickup_slot: pickupSlot,
          delivery_date: deliveryDate || undefined,
          delivery_slot: deliverySlot || undefined,
          admin_notes: adminNotes.trim() || undefined,
          special_instructions: specialInstructions.trim() || undefined,
          payment_mode: paymentMode,
          // '' clears the coupon; a code re-applies it (backend re-validates).
          coupon_code: couponCode.trim(),
          address_id: selectedAddressId || undefined,
        })
        onCreated()
        onClose()
        return
      }

      const payload: CreateAdminOrderPayload = {
        customer_id: customer.id,
        items: selectedItems.map(toLinePayload),
        payment_mode: paymentMode,
        admin_notes: adminNotes.trim() || undefined,
        special_instructions: specialInstructions.trim() || undefined,
        pickup_date: pickupDate,
        pickup_slot: pickupSlot,
        delivery_date: deliveryDate || undefined,
        delivery_slot: deliverySlot || undefined,
        coupon_code: couponCode.trim() || undefined,
      }

      if (selectedAddressId) {
        payload.address_id = selectedAddressId
      }

      const response = await adminApi.createOrderFromAdmin(payload)

      const created = unwrap<{
        id?: string
        order_number?: string
        display_id?: string | null
        qr_token?: string | null
        qr_payload?: QrPayload | null
      }>(response)
      setCreatedOrder({
        id: created?.id,
        order_number: created?.order_number || 'RD-XXXX',
        display_id: created?.display_id ?? null,
        qr_token: created?.qr_token ?? null,
        qr_payload: created?.qr_payload ?? null,
        customerName: customer?.name || 'Customer',
        items: selectedItems.map((item) => ({
          name: item.name,
          quantity: item.quantity,
          item_count: item.item_count,
        })),
      })
      onCreated()
    } catch (err: any) {
      const message = err?.response?.data?.message
        || err?.response?.data?.error
        || err?.message
        || 'Unable to create order. Please check the details and try again.'
      setError(message)
    } finally {
      setLoading(false)
      setIsPlacing(false)
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div
      ref={wizardRef}
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: colors.bgCard,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          padding: '24px 32px 16px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
        }}
      >
        <div>
          <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {isEdit ? `Edit order ${editContext?.orderNumber || ''}` : 'Create manual order'}
          </p>
          <h2 className="font-display" style={{ margin: '6px 0 0', fontSize: '24px', fontWeight: 700, color: colors.textPrimary }}>
            {isEdit ? 'Update Order' : 'New Order Wizard'}
          </h2>
        </div>

        <button
          type="button"
          className="font-body"
          onClick={onClose}
          style={{
            border: `1.5px solid ${colors.border}`,
            borderRadius: '999px',
            background: 'transparent',
            padding: '10px 18px',
            fontSize: '14px',
            fontWeight: 600,
            color: colors.textPrimary,
            cursor: 'pointer',
            minHeight: '48px',
          }}
        >
          ✕ Close
        </button>
      </div>

      <div style={{ padding: '16px 32px 8px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {STEPS.map((label, index) => {
            const isComplete = index < step
            const isActive = index === step
            const color = isComplete || isActive ? colors.accent : colors.textMuted
            const border = isComplete || isActive ? colors.accent : colors.border

            return (
              <div
                key={label}
                className="font-body"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 14px',
                  borderRadius: '999px',
                  border: `1.5px solid ${border}`,
                  background: isComplete ? 'rgba(214,185,123,0.12)' : colors.bgCard,
                  color,
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                <span style={{ fontWeight: 700 }}>{`Step ${index + 1}`}</span>
                {label}
              </div>
            )
          })}
        </div>
      </div>

      {error ? (
        <div style={{ padding: '0 32px 12px' }}>
          <div className="font-body" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: '12px', padding: '12px 16px', fontSize: '13px', fontWeight: 600 }}>
            {error}
          </div>
        </div>
      ) : null}

      <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px 32px' }}>
        {createdOrder ? (
          <div style={{ maxWidth: '520px', margin: '32px auto', background: colors.bgSecondary, borderRadius: '16px', padding: '28px', border: `1.5px solid ${colors.border}`, textAlign: 'center' }}>
            <h3 className="font-display" style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
              Order placed ✓
            </h3>
            <p className="font-body" style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 700, color: colors.textPrimary, fontFamily: '"Courier New", monospace' }}>
              {createdOrder.order_number}
            </p>
            {createdOrder.display_id ? (
              <p className="font-body" style={{ margin: '0 0 16px', fontSize: '13px', color: colors.textMuted, fontFamily: '"Courier New", monospace' }}>
                {createdOrder.display_id}
              </p>
            ) : null}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
              <OrderQRCode
                qrPayload={createdOrder.qr_payload}
                qrToken={createdOrder.qr_token}
                orderId={createdOrder.id}
                orderNumber={createdOrder.order_number}
                size={180}
              />
            </div>
            <p className="font-body" style={{ margin: '10px 0 0', fontSize: '12px', color: colors.textMuted }}>
              Scan this QR to track the order through the hub.
            </p>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="font-body"
                onClick={() => {
                  void openPrintLabels([
                    {
                      orderNumber: createdOrder.order_number || '',
                      customerName: createdOrder.customerName || 'Customer',
                      items: createdOrder.items || [],
                      qrPayload: createdOrder.qr_payload,
                      qrToken: createdOrder.qr_token,
                      orderId: createdOrder.id,
                    },
                  ])
                }}
                style={{
                  flex: 1,
                  minHeight: '48px',
                  borderRadius: '999px',
                  border: `1.5px solid ${colors.border}`,
                  background: colors.bgCard,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                  cursor: 'pointer',
                }}
              >
                🖨 Print label
              </button>
              <button
                type="button"
                className="font-body"
                onClick={() => {
                  setCreatedOrder(null)
                  setStep(0)
                  handleChangeCustomer()
                  setPhone('')
                  setCustomerForm({ name: '', email: '', address_line: '', city: 'Gurugram', pincode: '' })
                  setQuantities({})
                  setItemCounts({})
                  setAdminNotes('')
                  setSpecialInstructions('')
                  setCouponCode('')
                  setCouponApplied(false)
                }}
                style={{
                  flex: 1,
                  minHeight: '48px',
                  borderRadius: '999px',
                  border: `1.5px solid ${colors.border}`,
                  background: colors.bgCard,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                  cursor: 'pointer',
                }}
              >
                Create another
              </button>
              <button
                type="button"
                className="font-body"
                onClick={onClose}
                style={{
                  flex: 1,
                  minHeight: '48px',
                  borderRadius: '999px',
                  border: 'none',
                  background: '#D6B97B',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: colors.textPrimary,
                  cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>
          </div>
        ) : null}

        {!createdOrder && step === 0 ? (
          <div style={{ maxWidth: '760px', margin: '24px auto', display: 'grid', gap: '20px' }}>
            {!customer ? (
            <div style={{ background: colors.bgSecondary, borderRadius: '16px', padding: '20px', border: `1.5px solid ${colors.border}` }}>
              <p className="font-body" style={{ margin: 0, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', color: colors.textMuted }}>
                Step 1
              </p>
              <h3 className="font-display" style={{ margin: '8px 0 12px', fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                Who is ordering?
              </h3>

              <label className="font-body" style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px' }}>
                Search customer
              </label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <input
                  value={searchQuery}
                  onChange={(event) => {
                    setSearchQuery(event.target.value)
                    setPhone(event.target.value)
                    setShowNewCustomerForm(false)
                  }}
                  placeholder="Phone, name, or email"
                  className="font-body"
                  style={{
                    flex: 1,
                    minWidth: '240px',
                    height: '48px',
                    borderRadius: '10px',
                    border: `1.5px solid ${colors.border}`,
                    padding: '0 14px',
                    fontSize: '14px',
                    color: colors.textPrimary,
                  }}
                />
                <button
                  type="button"
                  className="font-body"
                  onClick={() => {
                    void handleSearchCustomer()
                  }}
                  disabled={loading || searching}
                  style={{
                    minWidth: '140px',
                    minHeight: '48px',
                    borderRadius: '999px',
                    border: 'none',
                    background: colors.textPrimary,
                    color: colors.surface,
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: loading || searching ? 'not-allowed' : 'pointer',
                    opacity: loading || searching ? 0.7 : 1,
                  }}
                >
                  {loading || searching ? 'Searching…' : 'Search'}
                </button>
              </div>

              {searching ? (
                <p className="font-body" style={{ margin: '12px 0 0', fontSize: '13px', color: colors.textMuted }}>
                  Searching…
                </p>
              ) : null}

              {searchError ? (
                <p className="font-body" style={{ margin: '12px 0 0', fontSize: '13px', color: '#991B1B' }}>
                  {searchError}
                </p>
              ) : null}

              {!searching && !searchError && searchResults.length > 0 ? (
                <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {searchResults.length} customer{searchResults.length !== 1 ? 's' : ''} found
                  </p>
                  {searchResults.map((sr) => {
                    const orderCount = Number(sr.orders_count) || 0
                    const lastOrder = sr.last_order_at
                      ? new Date(sr.last_order_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                      : null
                    return (
                      <button
                        key={sr.id}
                        type="button"
                        className="font-body"
                        onClick={() => {
                          setSearchResult(sr)
                          setCustomer(sr)
                          setPhone(sr.phone || '')
                          setSearchResults([])
                          setShowNewCustomerForm(false)
                          void loadAddresses(sr.id)
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: `1.5px solid ${colors.border}`,
                          background: colors.bgCard,
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                          transition: 'border-color 0.15s',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = colors.accent }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = colors.border }}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: colors.textPrimary }}>
                            {sr.name || 'Customer'}
                          </p>
                          <p style={{ margin: '2px 0 0', fontSize: '13px', color: colors.textSecondary }}>
                            {sr.phone}{sr.email ? ` · ${sr.email}` : ''}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: orderCount > 0 ? colors.accent : colors.textMuted }}>
                            {orderCount} order{orderCount !== 1 ? 's' : ''}
                          </p>
                          {lastOrder ? (
                            <p style={{ margin: '1px 0 0', fontSize: '11px', color: colors.textMuted }}>
                              Last: {lastOrder}
                            </p>
                          ) : null}
                        </div>
                      </button>
                    )
                  })}
                </div>
              ) : null}

              {!searching && hasSearched && searchResults.length === 0 && !showNewCustomerForm ? (
                <div style={{ marginTop: '12px' }}>
                  <p className="font-body" style={{ margin: '0 0 10px', fontSize: '13px', color: colors.textMuted }}>
                    No existing customer found.
                  </p>
                  <button
                    type="button"
                    className="font-body"
                    onClick={() => {
                      setShowNewCustomerForm(true)
                      setCustomerForm((prev) => ({ ...prev, name: '', email: '' }))
                    }}
                    style={{
                      minHeight: '40px',
                      padding: '0 20px',
                      borderRadius: '999px',
                      border: `1.5px solid ${colors.border}`,
                      background: 'transparent',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: colors.textPrimary,
                      cursor: 'pointer',
                    }}
                  >
                    + Create new customer
                  </button>
                </div>
              ) : null}
            </div>
            ) : null}

            {!customer && showNewCustomerForm ? (
              <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '20px', border: `1.5px solid ${colors.border}`, boxShadow: '0px 12px 32px rgba(15,46,42,0.08)' }}>
                <h4 className="font-display" style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                  New Customer
                </h4>
                <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
                  <input
                    value={customerForm.name}
                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="Customer name"
                    className="font-body"
                    style={{ height: '48px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                  />
                  <input
                    value={phone}
                    onChange={(event) => setPhone(event.target.value)}
                    placeholder="Phone number"
                    className="font-body"
                    style={{ height: '48px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                  />
                  <input
                    value={customerForm.email}
                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, email: event.target.value }))}
                    placeholder="Email (optional)"
                    className="font-body"
                    style={{ height: '48px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                  />
                  <input
                    value={customerForm.address_line}
                    onChange={(event) => setCustomerForm((prev) => ({ ...prev, address_line: event.target.value }))}
                    placeholder="Address line"
                    className="font-body"
                    style={{ height: '48px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                  />
                  <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
                    <input
                      value={customerForm.city}
                      onChange={(event) => setCustomerForm((prev) => ({ ...prev, city: event.target.value }))}
                      placeholder="City"
                      className="font-body"
                      style={{ height: '48px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                    />
                    <input
                      value={customerForm.pincode}
                      onChange={(event) => setCustomerForm((prev) => ({ ...prev, pincode: event.target.value }))}
                      placeholder="Pincode"
                      className="font-body"
                      style={{ height: '48px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="font-body"
                  onClick={() => {
                    void handleCreateCustomer()
                  }}
                  style={{
                    marginTop: '16px',
                    minHeight: '48px',
                    padding: '0 24px',
                    borderRadius: '999px',
                    border: 'none',
                    background: colors.textPrimary,
                    color: colors.surface,
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    width: '100%',
                  }}
                >
                  {loading ? 'Creating…' : 'Create & Continue'}
                </button>
              </div>
            ) : null}

            {customer ? (
              <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '20px', border: `1.5px solid ${colors.border}`, boxShadow: '0px 12px 32px rgba(15,46,42,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Customer
                    </p>
                    <p className="font-body" style={{ margin: '4px 0 0', fontSize: '15px', fontWeight: 700, color: colors.textPrimary }}>
                      {customer.name || 'Customer'} · {customer.phone}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="font-body"
                    onClick={handleChangeCustomer}
                    style={{ border: `1.5px solid ${colors.border}`, borderRadius: '999px', background: 'transparent', padding: '8px 16px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer' }}
                  >
                    Change
                  </button>
                </div>

                <h4 className="font-display" style={{ margin: '18px 0 10px', fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                  Delivery location
                </h4>

                {loadingAddresses ? (
                  <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textMuted }}>Loading saved addresses…</p>
                ) : null}

                {!loadingAddresses && addresses.length > 0 ? (
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {addresses.map((addr) => {
                      const isSel = address?.id === addr.id
                      return (
                        <label
                          key={addr.id}
                          className="font-body"
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', borderRadius: '12px', cursor: 'pointer', border: `1.5px solid ${isSel ? colors.accent : colors.border}`, background: isSel ? 'rgba(214,185,123,0.12)' : colors.bgCard }}
                        >
                          <input type="radio" name="delivery-address" checked={isSel} onChange={() => setAddress(addr)} />
                          <span style={{ flex: 1, fontSize: '13px', color: colors.textPrimary }}>
                            {addr.full_address || addr.label || 'Address'}
                          </span>
                          {addr.is_default ? (
                            <span style={{ fontSize: '11px', fontWeight: 700, color: colors.accent }}>Default</span>
                          ) : null}
                        </label>
                      )
                    })}
                  </div>
                ) : null}

                {!loadingAddresses && addresses.length === 0 && !showNewAddressForm ? (
                  <p className="font-body" style={{ margin: '0 0 10px', fontSize: '13px', color: colors.textMuted }}>
                    No saved address. Add a delivery location below.
                  </p>
                ) : null}

                {!showNewAddressForm ? (
                  <button
                    type="button"
                    className="font-body"
                    onClick={() => setShowNewAddressForm(true)}
                    style={{ marginTop: '12px', border: `1.5px dashed ${colors.border}`, borderRadius: '12px', background: 'transparent', padding: '12px 16px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer', width: '100%' }}
                  >
                    + Add new delivery location
                  </button>
                ) : (
                  <div style={{ marginTop: '12px', display: 'grid', gap: '10px' }}>
                    <input
                      value={addressForm.line1}
                      onChange={(e) => setAddressForm((p) => ({ ...p, line1: e.target.value }))}
                      placeholder="Flat / Building / House no."
                      className="font-body"
                      style={{ height: '46px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                    />
                    <input
                      value={addressForm.line2}
                      onChange={(e) => setAddressForm((p) => ({ ...p, line2: e.target.value }))}
                      placeholder="Street / Road"
                      className="font-body"
                      style={{ height: '46px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                    />
                    <input
                      value={addressForm.area}
                      onChange={(e) => setAddressForm((p) => ({ ...p, area: e.target.value }))}
                      placeholder="Area / Locality / Sector"
                      className="font-body"
                      style={{ height: '46px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                    />
                    <input
                      value={addressForm.landmark}
                      onChange={(e) => setAddressForm((p) => ({ ...p, landmark: e.target.value }))}
                      placeholder="Landmark (optional)"
                      className="font-body"
                      style={{ height: '46px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                    />
                    <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
                      <input
                        value={addressForm.pincode}
                        onChange={(e) => setAddressForm((p) => ({ ...p, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                        placeholder="Pincode (6 digits)"
                        inputMode="numeric"
                        className="font-body"
                        style={{ height: '46px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                      />
                      <input
                        value={addressForm.city}
                        onChange={(e) => setAddressForm((p) => ({ ...p, city: e.target.value }))}
                        placeholder="City"
                        className="font-body"
                        style={{ height: '46px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 14px', fontSize: '14px' }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="font-body"
                        onClick={() => { void handleSaveAddress() }}
                        disabled={savingAddress}
                        style={{ flex: 1, minHeight: '46px', borderRadius: '999px', border: 'none', background: colors.textPrimary, color: colors.surface, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
                      >
                        {savingAddress ? 'Saving…' : 'Save location'}
                      </button>
                      {addresses.length > 0 ? (
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => setShowNewAddressForm(false)}
                          style={{ minHeight: '46px', padding: '0 18px', borderRadius: '999px', border: `1.5px solid ${colors.border}`, background: 'transparent', fontSize: '14px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer' }}
                        >
                          Cancel
                        </button>
                      ) : null}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="font-body"
                  onClick={() => setStep(1)}
                  disabled={!address}
                  style={{ marginTop: '20px', minHeight: '48px', borderRadius: '999px', border: 'none', background: address ? '#D6B97B' : colors.border, color: colors.textPrimary, fontSize: '14px', fontWeight: 700, cursor: address ? 'pointer' : 'not-allowed', width: '100%' }}
                >
                  Continue to items
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {!createdOrder && step === 1 ? (
          <div style={{ maxWidth: '980px', margin: '24px auto' }}>
            <button
              type="button"
              className="font-body"
              onClick={() => setStep(0)}
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '16px',
              }}
            >
              ← Back
            </button>

            <h3 className="font-display" style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
              What do they want?
            </h3>

            {serviceItems.length === 0 ? (
              <div className="font-body" style={{ background: colors.bgSecondary, borderRadius: '16px', border: `1.5px solid ${colors.border}`, padding: '24px', textAlign: 'center', color: colors.textMuted }}>
                <p style={{ margin: '0 0 12px' }}>No services loaded. They may have failed to load or none are configured.</p>
                <button
                  type="button"
                  onClick={() => { void loadServices() }}
                  style={{ minHeight: '40px', padding: '0 18px', borderRadius: '999px', border: `1.5px solid ${colors.border}`, background: colors.bgCard, fontSize: '14px', fontWeight: 600, color: colors.textPrimary, cursor: 'pointer' }}
                >
                  Retry loading services
                </button>
              </div>
            ) : null}

            {serviceItems.length > 0 ? (
              <div style={{ marginBottom: '16px', display: 'grid', gap: '12px' }}>
                <input
                  value={itemSearch}
                  onChange={(e) => setItemSearch(e.target.value)}
                  placeholder="Search items (e.g. shirt, kurta, saree)…"
                  className="font-body"
                  style={{
                    width: '100%',
                    height: '48px',
                    borderRadius: '12px',
                    border: `1.5px solid ${colors.border}`,
                    padding: '0 16px',
                    fontSize: '14px',
                    color: colors.textPrimary,
                    background: colors.bgCard,
                  }}
                />
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {(['all', ...serviceNames]).map((name) => {
                    const isActive = activeService === name
                    const count =
                      name === 'all'
                        ? serviceSelectedCounts.total
                        : serviceSelectedCounts.counts[name] || 0
                    const label = name === 'all' ? 'All services' : name
                    return (
                      <button
                        key={name}
                        type="button"
                        className="font-body"
                        onClick={() => setActiveService(name)}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '999px',
                          border: `1.5px solid ${isActive ? colors.accent : colors.border}`,
                          background: isActive ? 'rgba(214,185,123,0.18)' : colors.bgCard,
                          fontSize: '13px',
                          fontWeight: 600,
                          color: colors.textPrimary,
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        {label}
                        {count > 0 ? (
                          <span
                            style={{
                              minWidth: '20px',
                              height: '20px',
                              padding: '0 6px',
                              borderRadius: '999px',
                              background: colors.accent,
                              color: colors.textPrimary,
                              fontSize: '11px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {count}
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>

                {activeServiceHasPricingToggle ? (
                  <div
                    style={{
                      display: 'inline-flex',
                      padding: '4px',
                      borderRadius: '999px',
                      background: colors.bgSecondary,
                      border: `1px solid ${colors.border}`,
                      alignSelf: 'flex-start',
                    }}
                  >
                    {(['weight', 'item'] as const).map((mode) => {
                      const isActive = pricingMode === mode
                      const label = mode === 'weight' ? 'By weight (kg)' : 'By item'
                      return (
                        <button
                          key={mode}
                          type="button"
                          className="font-body"
                          onClick={() => setPricingMode(mode)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '999px',
                            border: 'none',
                            background: isActive ? colors.textPrimary : 'transparent',
                            color: isActive ? colors.surface : colors.textPrimary,
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {label}
                        </button>
                      )
                    })}
                  </div>
                ) : null}

                {activeServiceHasPricingToggle && pricingMode === 'weight' ? (
                  <p
                    className="font-body"
                    style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}
                  >
                    Set the approximate weight in kg. Exact weight is confirmed at pickup.
                  </p>
                ) : null}

                {activeService !== 'all' &&
                activeServiceCategories.length > 0 &&
                !(activeServiceHasPricingToggle && pricingMode === 'weight') ? (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {(['all', ...activeServiceCategories]).map((cat) => {
                      const isActive = activeCategory === cat
                      const label = cat === 'all' ? 'All' : (CATEGORY_LABELS[cat] || cat)
                      return (
                        <button
                          key={cat}
                          type="button"
                          className="font-body"
                          onClick={() => setActiveCategory(cat)}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '999px',
                            border: `1px solid ${isActive ? colors.textPrimary : colors.border}`,
                            background: isActive ? colors.textPrimary : 'transparent',
                            color: isActive ? colors.surface : colors.textPrimary,
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {label}
                        </button>
                      )
                    })}
                  </div>
                ) : null}
              </div>
            ) : null}

            {serviceItems.length > 0 && filteredGroups.length === 0 ? (
              <div className="font-body" style={{ background: colors.bgSecondary, borderRadius: '16px', border: `1.5px solid ${colors.border}`, padding: '24px', textAlign: 'center', color: colors.textMuted }}>
                No items match “{itemSearch}”.
              </div>
            ) : null}

            {filteredGroups.map(([serviceName, items]) => (
              <div key={serviceName} style={{ marginBottom: '20px', background: colors.bgCard, borderRadius: '16px', border: `1.5px solid ${colors.border}`, padding: '18px' }}>
                <h4 className="font-display" style={{ margin: '0 0 12px', fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                  {serviceName}
                </h4>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {items.map((item) => {
                    const quantity = quantities[item.id] || 0
                    const approxItems = itemCounts[item.id] || 0
                    const isSelected = quantity > 0
                    const weightLine = isWeightUnit(item.unit)
                    const stepperBtn = (label: string, onClick: () => void, filled = false) => (
                      <button
                        type="button"
                        className="font-body"
                        aria-label={label}
                        onClick={onClick}
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '999px',
                          border: filled ? 'none' : `1.5px solid ${colors.border}`,
                          background: filled ? colors.textPrimary : colors.bgCard,
                          fontSize: '18px',
                          fontWeight: 600,
                          color: filled ? colors.surface : colors.textPrimary,
                          cursor: 'pointer',
                        }}
                      >
                        {filled ? '+' : '−'}
                      </button>
                    )

                    return (
                      <div
                        key={item.id}
                        style={{
                          padding: '14px 16px',
                          borderRadius: '14px',
                          border: `1.5px solid ${isSelected ? colors.accent : colors.border}`,
                          background: isSelected ? 'rgba(214,185,123,0.12)' : colors.bgCard,
                          display: 'flex',
                          alignItems: weightLine ? 'stretch' : 'center',
                          flexDirection: weightLine ? 'column' : 'row',
                          justifyContent: 'space-between',
                          gap: '12px',
                        }}
                      >
                        <div>
                          <p className="font-body" style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                            {item.name}
                          </p>
                          <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
                            ₹{formatAmount(Number(item.price || 0))} {formatUnit(item.unit)}
                            {item.category ? ` · ${CATEGORY_LABELS[item.category] || item.category}` : ''}
                          </p>
                        </div>
                        {weightLine ? (
                          <div style={{ display: 'grid', gap: '10px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                              <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted, minWidth: '88px' }}>Weight</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {stepperBtn(`Decrease weight for ${item.name}`, () => handleQuantityChange(item.id, -stepForUnit(item.unit)))}
                                <input
                                  type="number"
                                  min="0"
                                  step="0.05"
                                  inputMode="decimal"
                                  aria-label={`Weight in kg for ${item.name}`}
                                  value={quantity ? formatQty(quantity) : ''}
                                  placeholder="0"
                                  onChange={(e) => setQuantityDirect(item.id, e.target.value)}
                                  className="font-body"
                                  style={{
                                    width: '68px',
                                    textAlign: 'center',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: colors.textPrimary,
                                    background: colors.bgCard,
                                    border: `1.5px solid ${colors.border}`,
                                    borderRadius: '10px',
                                    height: '40px',
                                    padding: '0 6px',
                                  }}
                                />
                                {stepperBtn(`Increase weight for ${item.name}`, () => handleQuantityChange(item.id, stepForUnit(item.unit)), true)}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                              <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted, minWidth: '118px' }}>Number of clothes given</span>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {stepperBtn(`Decrease item count for ${item.name}`, () => handleItemCountChange(item.id, -1))}
                                <input
                                  aria-label={`Number of clothes given for ${item.name}`}
                                  inputMode="numeric"
                                  min="0"
                                  max="1000"
                                  step="1"
                                  type="number"
                                  value={approxItems ? String(approxItems) : ''}
                                  placeholder="0"
                                  onChange={(e) => setItemCountDirect(item.id, e.target.value)}
                                  onKeyDown={(e) => {
                                    if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault()
                                  }}
                                  className="font-body"
                                  style={{
                                    width: '68px',
                                    textAlign: 'center',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    color: colors.textPrimary,
                                    background: colors.bgCard,
                                    border: `1.5px solid ${colors.border}`,
                                    borderRadius: '10px',
                                    height: '40px',
                                    padding: '0 6px',
                                  }}
                                />
                                {stepperBtn(`Increase item count for ${item.name}`, () => handleItemCountChange(item.id, 1), true)}
                              </div>
                            </div>
                            <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted }}>
                              Count the individual clothes/pieces. Final weight is confirmed at pickup.
                            </p>

                            {/* Operator-agreed rate for THIS order only. Editing the
                                catalog would reprice every future order and the rate
                                the customer app advertises; this does not. */}
                            {isManualPriceEligible(item) ? (() => {
                              const catalogPrice = Number(item.price || 0)
                              const draft = manualRates[item.id]
                              const isManual = draft !== undefined
                              const fieldError = manualRateErrors[item.id]
                              const rate = effectiveUnitPrice(item, manualRates)
                              const lineTotal = previewLineTotal(item, quantity, manualRates)
                              const modeBtn = (label: string, active: boolean, onClick: () => void) => (
                                <button
                                  type="button"
                                  aria-pressed={active}
                                  onClick={onClick}
                                  className="font-body"
                                  style={{
                                    height: '30px',
                                    padding: '0 12px',
                                    borderRadius: '999px',
                                    border: `1.5px solid ${active ? colors.accent : colors.border}`,
                                    background: active ? 'rgba(214,185,123,0.16)' : colors.bgCard,
                                    color: active ? colors.accentText : colors.textPrimary,
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {label}
                                </button>
                              )
                              return (
                                <div
                                  style={{
                                    display: 'grid',
                                    gap: '8px',
                                    borderTop: `1px solid ${colors.border}`,
                                    paddingTop: '10px',
                                  }}
                                >
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted, minWidth: '88px' }}>
                                      Rate
                                    </span>
                                    {modeBtn('Use default price', !isManual, () => setManualMode(item.id, false, catalogPrice))}
                                    {modeBtn('Use manual price', isManual, () => setManualMode(item.id, true, catalogPrice))}
                                  </div>

                                  {isManual ? (
                                    <div style={{ display: 'grid', gap: '4px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted, minWidth: '88px' }}>
                                          Manual rate
                                        </span>
                                        <span className="font-body" style={{ color: colors.textMuted }}>₹</span>
                                        <input
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          inputMode="decimal"
                                          aria-label={`Manual rate per kg for ${item.name}`}
                                          aria-invalid={Boolean(fieldError)}
                                          value={draft}
                                          onChange={(e) => setManualRates((prev) => ({ ...prev, [item.id]: e.target.value }))}
                                          className="font-body"
                                          style={{
                                            width: '96px',
                                            textAlign: 'center',
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            color: colors.textPrimary,
                                            background: colors.bgCard,
                                            border: `1.5px solid ${fieldError ? 'var(--color-error)' : colors.border}`,
                                            borderRadius: '10px',
                                            height: '38px',
                                            padding: '0 6px',
                                          }}
                                        />
                                        <span className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                                          / kg · default ₹{formatAmount(catalogPrice)}
                                        </span>
                                      </div>
                                      {fieldError ? (
                                        <span role="alert" className="font-body" style={{ fontSize: '12px', color: 'var(--color-error)' }}>
                                          {fieldError}
                                        </span>
                                      ) : null}
                                    </div>
                                  ) : null}

                                  {quantity > 0 && !fieldError ? (
                                    <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textPrimary }}>
                                      {formatQty(quantity)} kg × ₹{formatAmount(rate)} ={' '}
                                      <strong>₹{formatAmount(lineTotal)}</strong>
                                      {isManual && rate !== catalogPrice ? (
                                        <span style={{ color: colors.textMuted }}> · manual rate</span>
                                      ) : null}
                                    </p>
                                  ) : null}
                                </div>
                              )
                            })() : null}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {stepperBtn(`Decrease ${item.name}`, () => handleQuantityChange(item.id, -stepForUnit(item.unit)))}
                            <span className="font-body" style={{ minWidth: '24px', textAlign: 'center', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                              {quantity}
                            </span>
                            {stepperBtn(`Increase ${item.name}`, () => handleQuantityChange(item.id, stepForUnit(item.unit)), true)}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <p className="font-display" style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: colors.textPrimary }}>
                Subtotal: ₹{formatAmount(subtotal)}
              </p>
              <button
                type="button"
                className="font-body"
                onClick={() => setStep(2)}
                // An invalid manual rate must not be silently discarded: without
                // this the line would quietly fall back to the catalog price and
                // the operator would never know the agreed rate was dropped.
                disabled={selectedItems.length === 0 || hasManualRateError}
                title={hasManualRateError ? 'Fix the manual rate before continuing' : undefined}
                style={{
                  minHeight: '48px',
                  padding: '0 26px',
                  borderRadius: '999px',
                  border: 'none',
                  background: selectedItems.length === 0 || hasManualRateError ? colors.border : colors.accent,
                  color: colors.textPrimary,
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: selectedItems.length === 0 || hasManualRateError ? 'not-allowed' : 'pointer',
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ) : null}

        {!createdOrder && step === 2 ? (
          <div style={{ maxWidth: '760px', margin: '24px auto', display: 'grid', gap: '20px' }}>
            <button
              type="button"
              className="font-body"
              onClick={() => setStep(1)}
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>

            <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '20px', border: `1.5px solid ${colors.border}` }}>
              <h3 className="font-display" style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                Schedule & Payment
              </h3>

              <div style={{ display: 'grid', gap: '12px' }}>
                <div
                  style={{
                    display: 'grid',
                    gap: '16px',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                  }}
                >
                  <div>
                    <h4
                      className="font-body"
                      style={{
                        margin: '0 0 12px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      Pickup Schedule
                    </h4>
                    <label className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      Pickup date
                      <input
                        type="date"
                        value={pickupDate}
                        onChange={(event) => setPickupDate(event.target.value)}
                        className="font-body"
                        style={{
                          width: '100%',
                          marginTop: '6px',
                          height: '48px',
                          borderRadius: '10px',
                          border: `1.5px solid ${colors.border}`,
                          padding: '0 12px',
                          fontSize: '14px',
                        }}
                      />
                    </label>

                    <label className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      Pickup slot
                      {pickupSlotsLoaded && pickupSlotOptions.length === 0 ? (
                        <p className="font-body" style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--color-error)' }}>
                          No slots available on this date. Pick another pickup date.
                        </p>
                      ) : (
                        <select
                          value={pickupSlot}
                          onChange={(event) => setPickupSlot(event.target.value)}
                          className="font-body"
                          style={{
                            width: '100%',
                            marginTop: '6px',
                            height: '48px',
                            borderRadius: '10px',
                            border: `1.5px solid ${colors.border}`,
                            padding: '0 12px',
                            fontSize: '14px',
                          }}
                        >
                          {(pickupSlotOptions.length ? pickupSlotOptions : PICKUP_SLOTS.map((label) => ({ label } as SlotInfo))).map((slot) => {
                            const full = slot.is_full === true || slot.available === false
                            return (
                              <option key={slot.label} value={slot.label} disabled={full}>
                                {slot.label}{full ? ' (full)' : ''}
                              </option>
                            )
                          })}
                        </select>
                      )}
                      {pickupHoliday ? (
                        <p className="font-body" style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-error)' }}>
                          ⚠ This date is a holiday, so pickup may be unavailable.
                        </p>
                      ) : null}
                    </label>
                  </div>

                  <div>
                    <h4
                      className="font-body"
                      style={{
                        margin: '0 0 12px',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      Delivery Schedule (estimated)
                    </h4>
                    <label className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      Delivery date
                      <input
                        type="date"
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        min={pickupDate}
                        className="font-body"
                        style={{
                          width: '100%',
                          marginTop: '6px',
                          height: '48px',
                          borderRadius: '10px',
                          border: `1.5px solid ${colors.border}`,
                          padding: '0 12px',
                          fontSize: '14px',
                        }}
                      />
                    </label>
                    <label
                      className="font-body"
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: colors.textPrimary,
                        marginTop: '12px',
                        display: 'block',
                      }}
                    >
                      Delivery slot
                      {deliverySlotsLoaded && deliverySlotOptions.length === 0 ? (
                        <p className="font-body" style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--color-error)' }}>
                          No slots available on this date. Pick another delivery date.
                        </p>
                      ) : (
                        <select
                          value={deliverySlot}
                          onChange={(e) => setDeliverySlot(e.target.value)}
                          className="font-body"
                          style={{
                            width: '100%',
                            marginTop: '6px',
                            height: '48px',
                            borderRadius: '10px',
                            border: `1.5px solid ${colors.border}`,
                            padding: '0 12px',
                            fontSize: '14px',
                          }}
                        >
                          {(deliverySlotOptions.length ? deliverySlotOptions : PICKUP_SLOTS.map((label) => ({ label } as SlotInfo))).map((slot) => {
                            const full = slot.is_full === true || slot.available === false
                            return (
                              <option key={slot.label} value={slot.label} disabled={full}>
                                {slot.label}{full ? ' (full)' : ''}
                              </option>
                            )
                          })}
                        </select>
                      )}
                      {deliveryHoliday ? (
                        <p className="font-body" style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--color-error)' }}>
                          ⚠ This date is a holiday, so delivery may be unavailable.
                        </p>
                      ) : null}
                    </label>
                  </div>
                </div>

                <div>
                  <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Payment mode
                  </p>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {(
                      [
                        { key: 'cod', label: 'Cash on Delivery' },
                        { key: 'online', label: 'Pay Online' },
                      ] as const
                    ).map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className="font-body"
                        onClick={() => setPaymentMode(option.key)}
                        style={{
                          flex: 1,
                          minHeight: '48px',
                          borderRadius: '999px',
                          border: `1.5px solid ${paymentMode === option.key ? colors.accent : colors.border}`,
                          background: paymentMode === option.key ? 'rgba(214,185,123,0.18)' : colors.bgCard,
                          fontSize: '14px',
                          fontWeight: 700,
                          color: colors.textPrimary,
                          cursor: 'pointer',
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginTop: '16px' }}>
                  <p className="font-body" style={{
                    margin: '0 0 8px', fontSize: '13px', fontWeight: 600,
                    color: colors.textPrimary
                  }}>
                    Coupon Code (optional)
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase())
                        setCouponApplied(false)
                        setCouponError('')
                        setCouponDiscount(0)
                      }}
                      placeholder="Enter coupon code"
                      className="font-body"
                      style={{
                        flex: 1, height: '44px', borderRadius: '10px',
                        border: `1.5px solid ${couponApplied ? 'var(--color-success)' : colors.border}`,
                        padding: '0 14px', fontSize: '14px',
                        color: colors.textPrimary,
                        background: couponApplied ? 'var(--color-success-bg)' : 'transparent',
                      }}
                      disabled={couponApplied}
                    />
                    {couponApplied ? (
                      <button type="button" className="font-body"
                        onClick={() => {
                          setCouponCode('')
                          setCouponApplied(false)
                          setCouponDiscount(0)
                        }}
                        style={{
                          padding: '0 14px', borderRadius: '10px',
                          border: '1px solid var(--color-error)',
                          background: 'transparent', color: 'var(--color-error)',
                          fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                        }}>
                        Remove
                      </button>
                    ) : null}
                  </div>
                  {couponError && (
                    <p className="font-body" style={{
                      margin: '6px 0 0', fontSize: '12px', color: 'var(--color-error)'
                    }}>
                      {couponError}
                    </p>
                  )}
                  {couponApplied && (
                    <p className="font-body" style={{
                      margin: '6px 0 0', fontSize: '12px', color: 'var(--color-success)'
                    }}>
                      ✓ Coupon applied! Discount will be calculated on submission.
                    </p>
                  )}
                </div>

                <div style={{ display: 'grid', gap: '12px' }}>
                  <label className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Notes about your clothes
                    <textarea
                      value={specialInstructions}
                      onChange={(event) => setSpecialInstructions(event.target.value)}
                      placeholder="Any special care instructions?"
                      className="font-body"
                      style={{ width: '100%', marginTop: '6px', minHeight: '96px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '12px', fontSize: '14px', resize: 'vertical' }}
                    />
                  </label>

                  <label className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                    Admin notes
                    <textarea
                      value={adminNotes}
                      onChange={(event) => setAdminNotes(event.target.value)}
                      placeholder="Internal notes for the team"
                      className="font-body"
                      style={{ width: '100%', marginTop: '6px', minHeight: '96px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '12px', fontSize: '14px', resize: 'vertical' }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="font-body"
              onClick={() => setStep(3)}
              style={{
                minHeight: '48px',
                padding: '0 26px',
                borderRadius: '999px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              Continue
            </button>
          </div>
        ) : null}

        {!createdOrder && step === 3 ? (
          <div style={{ maxWidth: '760px', margin: '24px auto', display: 'grid', gap: '16px' }}>
            <button
              type="button"
              className="font-body"
              onClick={() => setStep(2)}
              style={{
                border: 'none',
                background: 'transparent',
                color: colors.textPrimary,
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              ← Back
            </button>

            <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '20px', border: `1.5px solid ${colors.border}` }}>
              <h3 className="font-display" style={{ margin: '0 0 12px', fontSize: '22px', fontWeight: 700, color: colors.textPrimary }}>
                Review & Confirm
              </h3>

              <div style={{ display: 'grid', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                      Customer
                    </p>
                    <p className="font-body" style={{ margin: '6px 0 0', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      {customer?.name || 'Customer'} · {customer?.phone}
                    </p>
                    <p className="font-body" style={{ margin: '8px 0 0', fontSize: '12px', color: colors.textMuted }}>
                      Delivery to
                    </p>
                    <p className="font-body" style={{ margin: '4px 0 0', fontSize: '13px', color: colors.textPrimary, maxWidth: '320px' }}>
                      {address?.full_address || 'Customer default address'}
                    </p>
                  </div>
                  <div>
                    <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                      Pickup
                    </p>
                    <p className="font-body" style={{ margin: '6px 0 0', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      {pickupDate} · {pickupSlot}
                    </p>
                    <p className="font-body" style={{ margin: '6px 0 0', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      Delivery: {deliveryDate} · {deliverySlot}
                    </p>
                  </div>
                </div>

                <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '12px' }}>
                  {selectedItems.map((item) => {
                    const catalogPrice = Number(item.price || 0)
                    const rate = effectiveUnitPrice(item, manualRates)
                    const isManual = rate !== catalogPrice
                    return (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' }}>
                        <span className="font-body" style={{ fontSize: '14px', color: colors.textPrimary }}>
                          {item.name} × {isWeightUnit(item.unit)
                            ? `${formatQty(item.quantity)} kg${item.item_count ? ` · ${item.item_count} items` : ''}`
                            : item.quantity}
                          {/* The agreed rate is stated next to the list rate it
                              replaced, so nobody confirms a discount blind. */}
                          {isManual ? (
                            <span className="font-body" style={{ display: 'block', fontSize: '12px', color: colors.textMuted }}>
                              manual rate ₹{formatAmount(rate)}/kg · default ₹{formatAmount(catalogPrice)}/kg
                            </span>
                          ) : null}
                        </span>
                        <span className="font-body" style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary, whiteSpace: 'nowrap' }}>
                          ₹{formatAmount(previewLineTotal(item, item.quantity, manualRates))}
                        </span>
                      </div>
                    )
                  })}
                </div>

                <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span className="font-body" style={{ fontSize: '13px', color: colors.textSecondary }}>
                      Subtotal
                    </span>
                    <span className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      ₹{formatAmount(subtotal)}
                    </span>
                  </div>
                  {couponCode.trim() ? (
                    <p className="font-body" style={{ margin: '0 0 6px', fontSize: '13px', color: colors.textSecondary }}>
                      Coupon: {couponCode}
                    </p>
                  ) : null}
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: deliveryFee > 0 ? '2px' : '6px' }}>
                    <span className="font-body" style={{ fontSize: '13px', color: colors.textSecondary }}>
                      {deliveryFee > 0 ? 'Additional charge' : 'Delivery fee'}
                    </span>
                    <span className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                      {deliveryFee > 0 ? `₹${formatAmount(deliveryFee)}` : 'FREE'}
                    </span>
                  </div>
                  {deliveryFee > 0 ? (
                    <p className="font-body" style={{ margin: '0 0 6px', fontSize: '12px', color: colors.textMuted }}>
                      Minimum bill is ₹{MIN_PAYABLE_FLOOR}, so a charge of ₹{formatAmount(deliveryFee)} is added.
                    </p>
                  ) : null}
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span className="font-body" style={{ fontSize: '14px', fontWeight: 700, color: colors.textPrimary }}>
                      Grand total
                    </span>
                    <span className="font-body" style={{ fontSize: '14px', fontWeight: 700, color: colors.textPrimary }}>
                      ₹{formatAmount(grandTotal)}
                    </span>
                  </div>
                  {couponCode.trim() ? (
                    <p className="font-body" style={{ margin: '6px 0 0', fontSize: '12px', color: colors.textMuted }}>
                      Coupon discount is applied by the server at confirmation; the final
                      payable shown on the order may be lower.
                    </p>
                  ) : null}
                </div>

                <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '12px' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '13px', color: colors.textSecondary }}>
                    Payment mode: {paymentMode === 'cod' ? 'Cash on Delivery' : 'Pay Online'}
                  </p>
                  <p className="font-body" style={{ margin: '6px 0 0', fontSize: '13px', color: colors.textSecondary }}>
                    Admin notes: {adminNotes.trim() ? adminNotes : 'None'}
                  </p>
                  <p className="font-body" style={{ margin: '6px 0 0', fontSize: '13px', color: colors.textSecondary }}>
                    Notes about clothes: {specialInstructions.trim() ? specialInstructions : 'None'}
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="font-body"
              onClick={() => {
                void handlePlaceOrder()
              }}
              disabled={isPlacing}
              style={{
                minHeight: '48px',
                padding: '0 26px',
                borderRadius: '999px',
                border: 'none',
                background: '#D6B97B',
                color: colors.textPrimary,
                fontSize: '15px',
                fontWeight: 700,
                cursor: 'pointer',
                width: '100%',
              }}
            >
              {isPlacing ? 'Saving...' : isEdit ? 'Save Changes' : 'Place Order'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
